import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { SpreadsheetImport } from "./SpreadsheetImport";

const PLACEHOLDER = `One per line — reference first, the rest optional:
Reference | Question text | Asked by (a; b) | Asked to | Type | Theme
John 3:4 | How can a man be born when he is old? | Nicodemus | Jesus | Wonder | Identity & Calling
Luke 10:29`;

export default function AdminImport() {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [status, setStatus] = useState("draft");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const { data: books } = useQuery({ queryKey: ["import-books"], queryFn: async () => (await api.get("/admin/import/books")).data, refetchInterval: 30000 });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || "").trim());
    reader.readAsText(f);
  };

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = (await api.post("/admin/import/bulk", { text, status })).data;
      setResult(r);
      toast.success(`${r.created.length} created${r.errors.length ? `, ${r.errors.length} failed` : ""}.`);
      if (r.created.length) setText("");
      qc.invalidateQueries({ queryKey: ["admin-questions"] }); qc.invalidateQueries({ queryKey: ["admin-overview"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-import">
      <SpreadsheetImport />
      <section className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <div className="flex items-start gap-3">
          <Upload className="mt-1 h-5 w-5 shrink-0 text-gold" />
          <div>
            <h2 className="font-serif text-2xl font-semibold text-foreground">Bulk add by reference</h2>
            <p className="mt-1 text-sm text-muted-foreground">Paste references (or upload a CSV). The site fetches the KJV/ESV verse, finds the question in the verse and builds the Strong&apos;s words for each one.</p>
          </div>
        </div>
        <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder={PLACEHOLDER} className="font-mono text-xs" data-testid="bulk-text" />
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".csv,.txt" onChange={onFile} className="hidden" data-testid="bulk-file" />
          <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="bulk-upload">Upload CSV</Button>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="bulk-status">
            <option value="draft">Save as drafts (hidden)</option>
            <option value="published">Publish immediately</option>
          </select>
          <Button onClick={run} disabled={busy || !text.trim()} className="ml-auto rounded-full bg-primary text-primary-foreground" data-testid="bulk-run">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Import {text.split("\n").filter((l) => l.trim()).length || ""} line{text.split("\n").filter((l) => l.trim()).length === 1 ? "" : "s"}
          </Button>
        </div>
        {result && (
          <div className="space-y-2 text-sm" data-testid="bulk-result">
            {result.created.map((c) => <p key={c.id} className="text-foreground">✓ <span className="font-mono text-xs">{c.reference}</span> — &ldquo;{c.question_kjv}&rdquo; <span className="text-muted-foreground">({c.words} Strong&apos;s words)</span></p>)}
            {result.errors.map((e, i) => <p key={`${e.line}-${i}`} className="text-destructive">✕ {e.line} — {e.error}</p>)}
          </div>
        )}
      </section>

      {books?.books?.length > 0 && (
        <section className="rounded-2xl border border-border bg-card p-5" data-testid="import-books">
          <h2 className="font-serif text-2xl font-semibold text-foreground">Progress by book</h2>
          <p className="mt-1 text-sm text-muted-foreground">Published vs. drafts still being prepared. Drafts publish automatically once their study text is fetched; the rest wait in the Review queue.</p>
          <ul className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            {books.books.map((b) => {
              const total = b.published + b.drafts;
              return (
                <li key={b.book} className="text-sm" data-testid={`import-book-${b.book.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium text-foreground">{b.book}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">{b.published} / {total}{b.drafts ? ` · ${b.drafts} drafts` : ""}</span>
                  </div>
                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted"><div className="h-full bg-gold" style={{ width: `${Math.round((b.published / total) * 100)}%` }} /></div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
